/**
 * Common utility functions
 */

const locale_map = {};
locale_map.en = 'en-US';
locale_map.de = 'de-DE';
locale_map.es = 'es-ES';
locale_map.zh = 'zh-CN';
locale_map.fr = 'fr-FR';
locale_map.it = 'it-IT';
locale_map.pt = 'pt-PT';
locale_map.ru = 'en-US';
locale_map.sv = 'en-US';
locale_map.zh = 'zh-CN';
locale_map.kr = 'ko-KR';
locale_map.ko = 'ko-KR';
locale_map.nl = 'en-US';

/**
 * Utility function to retrieve language code
 *
 * @param   {string} locale
 * @returns {string} language code
 */
export const getLangCode = (l) => {
  const locale = l ?? I18N.locale ?? 'en';
  let mapped_locale = "en-US";
  if (locale_map[locale] && locale_map[locale] != "")
    mapped_locale = locale_map[locale];
  return mapped_locale;
}

/**
 * 
 * @param {*} serviceURL 
 * @param {*} locale 
 * @returns 3w support request url with lang code
 */
export const getSupportRequestUrl = (serviceURL) => {
  const locale = I18N.locale || 'en';
  let mapped_locale = "en-US";
  if (locale_map[locale] && locale_map[locale] != "") {
    mapped_locale = locale_map[locale];
  }
  const endIdx = serviceURL.indexOf('_LANGCODE_');
  const url = serviceURL.substr(0, endIdx) + mapped_locale + '/hp-csp-my-requests/';
  return url;
}

export const getSupportRequestUrlByHostname = (hostname, locale) => {
  locale = locale || 'en'
  const lang = locale_map[locale];
  const isProd = (hostname === 'oss.hpcloud.hp.com' || hostname ==='orderstatus.hpcloud.hp.com');
  let supportRequestUrl = isProd
    ? 'https://saml.id.hp.com/idp/profile/SAML2/Unsolicited/SSO?providerId=https://hp-om.microsoftcrmportals.com&target=ReturnUrl=/' + lang + '/hp-csp-my-requests/'
    : 'https://saml.stg.cd.id.hp.com/idp/profile/SAML2/Unsolicited/SSO?providerId=https://hp3w-3.microsoftcrmportals.com&target=ReturnUrl=/' + lang + '/hp-csp-my-requests/';

  return supportRequestUrl;
}

export const get3wViewerLink = (serviceURL, id) => {
  const locale = I18N.locale || 'en';
  let mapped_locale = "en-US";
  if (locale_map[locale] && locale_map[locale] != "") {
    mapped_locale = locale_map[locale];
  }

  const sIdx = 0;
  const eIdx = serviceURL.indexOf('_LANGCODE_') + 10;
  let ViewerLink3w = serviceURL.substr(sIdx, eIdx - sIdx);
  ViewerLink3w = ViewerLink3w.replace('_LANGCODE_', mapped_locale);
  ViewerLink3w = ViewerLink3w + '/requests/hp-csp-view-request/?id=' + id;
  return ViewerLink3w;
}

export const is3wAdvUser = (UserSettings) => {
  return (UserSettings.memberOf('Q2C_OSS_BETA_TESTERS') || UserSettings.memberOf('Q2C_OSS_BETA_TESTERS_NON_PROD'));
}

export const isProdHpics = (UserSettings) => {
  const blockedTenant = 'HPICS';
  const blockedLayer = 'prod';
  const tenant = UserSettings.tenant;
  const layer = UserSettings.config.layerName === "" ? 'prod': 'non-prod';
  if(tenant === blockedTenant && layer === blockedLayer){
    return true;
  }
  return false;
}

//----------------------------------------------------------------------
//   isAdvancedExchangeServiceType
//
//   determines whether a service type follows the Standard or Advanced
//   Exchange fulfillment model
//----------------------------------------------------------------------
export const isAdvancedExchangeServiceType = (serviceType) => {
  var pos = -1;

  if ( UserSettings!= undefined && UserSettings.config.standardAdvancedExchangeServiceTypes != undefined && serviceType != "" ) {
    pos = UserSettings.config.standardAdvancedExchangeServiceTypes.indexOf(serviceType);
  }

  return ( pos > -1 ? true : false );
}


/**
 * validates if 3w cases title start with 'hp external'
 */
export const isHpExternal = (case3w) => {
  const caseTitle = case3w.case_process_name && case3w.case_process_name.toLowerCase().trim();
  return caseTitle.indexOf('hp external') === 0
}

/*** Status Colors ***/
export const statusColors = {
  'accepted'   : '#0096d6',
  'processing' : '#0096d6',
  'canceled'   : '#cc5555',
  'delivered'  : '#008424',
  'production' : '#dab700',
  'shipped'    : '#33aa33',
  'submitted'  : '#999999',
  'shipmentcanceled' : '#db8888'
};

/**
 * checks if the user has a hp.com user_id
 */
 export const isHpUser = (user_id) => {
  const idxHpEmail = user_id.toLowerCase().indexOf('@hp.com');
  return idxHpEmail >= 0;
}

/**
 * checks if the user has permissions for accessing from the extranet
 */
 export const isExternalAccessUser = (UserSettings) => {
  return UserSettings.memberOf('CS_ExternalAccess');
}

/**
 * download blob content as a file in browser
 */
export function downloadFile(blob, filename) {
  // Validate the blob type
  if (!(blob instanceof Blob)) {
    throw new Error("Invalid blob object provided.");
  }

  // Validate the MIME type (adjust allowed types as needed)
  const allowedMimeTypes = [
    'application/pdf', 'application/vnd.ms-excel', 'application/octet-stream',
    'image/png', 'image/jpeg', 'text/plain', 'text/html', 'application/zip'
  ];

  // Compare the base MIME type only, as servers may append parameters (e.g. "; charset=utf-8")
  const baseType = (blob.type || '').split(';')[0].trim().toLowerCase();

  if (!allowedMimeTypes.includes(baseType)) {
    throw new Error("Unsupported file type.");
  }

  // Sanitize the filename by removing any potentially malicious characters
  const sanitizedFilename = filename
    ? filename.replace(/[^a-zA-Z0-9.\-_]/g, "").substring(0, 100)
    : 'download';

  const sanitizedUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = vcEncodeHtml(sanitizedUrl);
  a.download = sanitizedFilename;

  const clickHandler = () => {
    setTimeout(() => {
      URL.revokeObjectURL(sanitizedUrl);
      a.removeEventListener('click', clickHandler);
    }, 150);
  };

  a.addEventListener('click', clickHandler, false);
  a.click();
}

/**
 * Encode String to HTML encoded String.
 *
 * Use TextNode to let browser do encoding.
 * @param {string} value String to encode
 * @returns {string} Encoded string
 */
export function vcEncodeHtml(value) {
  var text = document.createTextNode(value);
  var p = document.createElement('p');
  p.appendChild(text);
  return p.innerHTML;
}

/**
 * Sanitize a URL by removing unsafe characters.
 *
 * @param {string} url The URL to sanitize
 * @returns {string} The sanitized URL
 */
export function sanitizeUrl(url) {
  return url.replace(/[^a-zA-Z0-9-_.~:/?#\[\]@!$&'()*+,;=%]/g, "");
}